import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Target, Trophy, Calendar, Clock, Users, CheckCircle, XCircle } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/hooks/useAuth';
import { useBetting } from '@/hooks/useBetting';
import { Match, MatchStatus, UserTier, BetStatus, formatCurrency } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

/**
 * Match Results Page - Company users can add results for completed matches
 */
const MatchResults: React.FC = () => {
  const { user, isCompany } = useAuth();
  const { matches, bets, updateMatchResult, settleBets } = useBetting();
  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  const [result, setResult] = useState<string>(''); // Updated to support score format
  const [isResultDialogOpen, setIsResultDialogOpen] = useState(false);

  // Redirect if not company user
  if (!isCompany) {
    return (
      <Layout>
        <div className="p-6">
          <Card>
            <CardContent className="pt-6">
              <p className="text-muted-foreground">Access denied. Only company users can manage match results.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // Filter matches that can have results added
  const completableMatches = useMemo(() => {
    return matches.filter(match => 
      match.status === MatchStatus.LIVE || 
      (match.status === MatchStatus.COMPLETED && !match.result)
    );
  }, [matches]);

  // Filter completed matches with results
  const completedMatches = useMemo(() => {
    return matches.filter(match => 
      match.status === MatchStatus.COMPLETED && match.result
    );
  }, [matches]);

  // Get bets for a specific match
  const getMatchBets = (matchId: string) => {
    return bets.filter(bet => bet.matchId === matchId);
  };

  const handleAddResult = (match: Match) => {
    setSelectedMatch(match);
    setResult(match.result || '');
    setIsResultDialogOpen(true);
  };

  const handleSubmitResult = () => {
    if (!selectedMatch || !result) {
      toast.error('Please select a result');
      return;
    }

    // Update match result
    updateMatchResult(selectedMatch.id);
    
    // Settle all bets for this match
    const matchBets = getMatchBets(selectedMatch.id);
    settleBets(selectedMatch.id);

    toast.success(`Match result added: ${result.toUpperCase()}`);
    setIsResultDialogOpen(false);
    setSelectedMatch(null);
    setResult('');
  };

  const getResultBadge = (result: string) => {
    switch (result) {
      case 'home':
        return <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20">Home Win</Badge>;
      case 'away':
        return <Badge className="bg-red-500/10 text-red-600 border-red-500/20">Away Win</Badge>;
      case 'draw':
        return <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">Draw</Badge>;
      default:
        return <Badge variant="outline">No Result</Badge>;
    }
  };

  const getStatusBadge = (status: MatchStatus) => {
    switch (status) {
      case MatchStatus.UPCOMING:
        return <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20">Upcoming</Badge>;
      case MatchStatus.LIVE:
        return <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Live</Badge>;
      case MatchStatus.COMPLETED:
        return <Badge className="bg-gray-500/10 text-gray-600 border-gray-500/20">Completed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const calculateMatchStats = (matchId: string, result: string) => {
    const matchBets = getMatchBets(matchId);
    const totalBets = matchBets.length;
    const totalAmount = matchBets.reduce((sum, bet) => sum + bet.amount, 0);
    const winningBets = matchBets.filter(bet => bet.selection === result);
    const losingBets = matchBets.filter(bet => bet.selection !== result);
    
    return {
      totalBets,
      totalAmount,
      winningBets: winningBets.length,
      losingBets: losingBets.length,
      winningAmount: winningBets.reduce((sum, bet) => sum + (bet.amount * bet.oddsAtTime), 0),
      losingAmount: losingBets.reduce((sum, bet) => sum + bet.amount, 0)
    };
  };

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Match Results</h1>
            <p className="text-muted-foreground">Add results for completed matches and settle bets</p>
          </div>
        </div>

        {/* Matches Needing Results */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Matches Needing Results</h2>
          {completableMatches.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-8">
                  <Target className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No matches need results</h3>
                  <p className="text-muted-foreground">All matches have been completed and results added</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {completableMatches.map((match) => {
                const matchBets = getMatchBets(match.id);
                return (
                  <motion.div
                    key={match.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-4 mb-2">
                              <h3 className="text-lg font-semibold">
                                {match.homeTeam} vs {match.awayTeam}
                              </h3>
                              {getStatusBadge(match.status)}
                            </div>
                            <div className="flex items-center gap-6 text-sm text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {new Date(match.startTime).toLocaleDateString()}
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {new Date(match.startTime).toLocaleTimeString()}
                              </div>
                              <div className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {matchBets.length} bets
                              </div>
                            </div>
                            <div className="flex items-center gap-6 mt-3">
                              <div className="text-sm">
                                <span className="text-muted-foreground">Home:</span>
                                <span className="font-mono ml-1">{match.odds.home}</span>
                              </div>
                              <div className="text-sm">
                                <span className="text-muted-foreground">Draw:</span>
                                <span className="font-mono ml-1">{match.odds.draw}</span>
                              </div>
                              <div className="text-sm">
                                <span className="text-muted-foreground">Away:</span>
                                <span className="font-mono ml-1">{match.odds.away}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() => handleAddResult(match)}
                              className="gap-2"
                            >
                              <Target className="w-4 h-4" />
                              Add Result
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>

        {/* Completed Matches */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Completed Matches</h2>
          {completedMatches.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-4">
                  <p className="text-muted-foreground">No completed matches with results yet</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {completedMatches.map((match) => {
                const stats = calculateMatchStats(match.id, match.result!);
                return (
                  <motion.div
                    key={match.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-4 mb-2">
                              <h3 className="text-lg font-semibold">
                                {match.homeTeam} vs {match.awayTeam}
                              </h3>
                              {getStatusBadge(match.status)}
                              {getResultBadge(match.result!)}
                            </div>
                            <div className="flex items-center gap-6 text-sm text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {new Date(match.startTime).toLocaleDateString()}
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {new Date(match.startTime).toLocaleTimeString()}
                              </div>
                            </div>
                            <div className="grid grid-cols-4 gap-4 mt-3 text-sm">
                              <div>
                                <span className="text-muted-foreground">Total Bets:</span>
                                <span className="font-mono ml-1">{stats.totalBets}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Total Amount:</span>
                                <span className="font-mono ml-1">{formatCurrency(stats.totalAmount)}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <CheckCircle className="w-3 h-3 text-green-600" />
                                <span className="text-muted-foreground">Won:</span>
                                <span className="font-mono ml-1">{stats.winningBets}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <XCircle className="w-3 h-3 text-red-600" />
                                <span className="text-muted-foreground">Lost:</span>
                                <span className="font-mono ml-1">{stats.losingBets}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>

        {/* Add Result Dialog */}
        <Dialog open={isResultDialogOpen} onOpenChange={setIsResultDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Match Result</DialogTitle>
              <DialogDescription>
                Select the result for {selectedMatch?.homeTeam} vs {selectedMatch?.awayTeam}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Match Result</Label>
                <Select value={result} onValueChange={(value) => setResult(value as 'home' | 'away' | 'draw')}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select match result" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="home">{selectedMatch?.homeTeam} Win</SelectItem>
                    <SelectItem value="draw">Draw</SelectItem>
                    <SelectItem value="away">{selectedMatch?.awayTeam} Win</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {selectedMatch && (
                <div className="bg-muted/50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">Match Summary</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Home Odds:</span>
                      <span className="font-mono ml-1">{selectedMatch.odds.home}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Draw Odds:</span>
                      <span className="font-mono ml-1">{selectedMatch.odds.draw}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Away Odds:</span>
                      <span className="font-mono ml-1">{selectedMatch.odds.away}</span>
                    </div>
                  </div>
                  <div className="mt-2 text-sm">
                    <span className="text-muted-foreground">Total Bets:</span>
                    <span className="font-mono ml-1">{getMatchBets(selectedMatch.id).length}</span>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsResultDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSubmitResult} disabled={!result}>
                  Add Result & Settle Bets
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default MatchResults;